import React, { useState, useEffect, useRef } from 'react'
import { Search, Truck, RefreshCw, XCircle, ChevronDown } from 'react-feather'
import { Package, CheckCircle, EnvelopeSimple, ShieldWarning, ArrowSquareOut, DotsThreeVertical, SortAscending, Check, CaretLeft, MapPin, CreditCard, XCircle as XCircleFill, X } from '@phosphor-icons/react'
import { useAccountPageUIContext } from '../AccountPageProvider'
import { useCancelDialog } from './CancelDialogContext'
import { hexToRgba } from '../../utils/colorUtils'
import { AccountPageExpandedSection, AccountPageOrderStatus, AccountPageRealEstate } from '../types'
import type { BorderSides, AccountPageOrderDetail, OrderTransaction, AccountPageOrder } from '../types'
import { AccountPageOverlayPanel } from './AccountPageChrome'
import AccountCustomComponentSlot from './AccountCustomComponentSlot'

const truncateMiddle = (text: string, maxLength = 30): string => {
  if (text.length <= maxLength) return text
  const keep = Math.floor((maxLength - 3) / 2)
  return `${text.slice(0, keep + 1)}...${text.slice(text.length - keep)}`
}

const getBorderStyle = (sides: BorderSides, width: string, color: string) => {
  if (sides === 'none') return {}
  if (sides === 'bottom') return { borderBottomWidth: width, borderBottomColor: color, borderBottomStyle: 'solid' as const }
  if (sides === 'top') return { borderTopWidth: width, borderTopColor: color, borderTopStyle: 'solid' as const }
  return { borderWidth: width, borderColor: color, borderStyle: 'solid' as const }
}

const StatusIcon = ({ status, color, size = 9 }: { status: AccountPageOrderStatus; color: string; size?: number }) => {
  const props = { size, color, strokeWidth: 2 }
  if (status === 'delivered') return <CheckCircle weight='fill' {...props} />
  if (status === 'in_transit') return <Truck {...props} />
  if (status === 'refunded') return <RefreshCw {...props} />
  return <XCircle {...props} />
}

const statusLabel = (status: AccountPageOrderStatus) => {
  if (status === 'in_transit') return 'In Transit'
  return status.charAt(0).toUpperCase() + status.slice(1)
}

const openTrackingUrl = (url: string) => window.open(url, '_blank', 'noopener,noreferrer')

type EmptyOrdersAction = { label: string; showIcon?: boolean; onClick: () => void }

const EmptyOrdersState = ({
  description,
  email,
  headerColor,
  actions: ctaActions,
}: {
  description: string
  email?: string
  headerColor: string
  actions: EmptyOrdersAction[]
}) => (
  <div className='ui-flex ui-flex-col ui-items-center ui-justify-center ui-h-full ui-w-full ui-gap-[8px] ui-py-12 ui-px-6'>
    <div className='ui-flex ui-items-center ui-justify-center ui-size-[120px]'>
      <img src='https://image.shopflo.com/shopping-cart.png' alt='No orders' className='ui-size-[120px] ui-object-contain' />
    </div>

    <div className='ui-flex ui-flex-col ui-items-center ui-gap-[48px]'>
      <div className='ui-flex ui-flex-col ui-items-center ui-gap-[8px] ui-text-center'>
        <p
          className='ui-text-[14px] ui-font-semibold ui-leading-[22px] ui-tracking-[-0.14px] ui-whitespace-nowrap'
          style={{ color: headerColor }}
        >
          No orders showing up?
        </p>
        <p
          className='ui-text-[12px] ui-font-medium ui-leading-[18px] ui-tracking-[-0.12px] ui-opacity-70 ui-text-center ui-max-w-[316px]'
          style={{ color: headerColor }}
        >
          {description}
        </p>
      </div>

      <div className='ui-flex ui-flex-col ui-items-center ui-gap-[8px]'>
        <div className='ui-flex ui-items-center ui-gap-[8px]'>
          {ctaActions.map(({ label, showIcon, onClick }) => (
            <button
              key={label}
              onClick={onClick}
              className='ui-flex ui-items-center ui-gap-[6px] ui-h-[28px] ui-px-[12px] ui-rounded-[4px] ui-cursor-pointer'
              style={{ backgroundColor: headerColor }}
            >
              <span className='ui-text-[12px] ui-font-semibold ui-leading-[18px] ui-tracking-[-0.12px] ui-opacity-80 ui-text-white'>
                {label}
              </span>
              {showIcon && <ArrowSquareOut size={16} color='white' weight='regular' className='ui-opacity-60' />}
            </button>
          ))}
        </div>
        {email && (
          <p
            className='ui-text-[11px] ui-font-medium ui-leading-[16px] ui-opacity-60 ui-text-center'
            style={{ color: headerColor }}
          >
            {email}
          </p>
        )}
      </div>
    </div>
  </div>
)

// ─── Shared: "Past Orders" header ────────────────────────────────────────────
// Visual: mobile style (Package icon + title). onClick: desktop behavior (FOOTER section).

const PastOrdersHeader = ({
  color,
  onSectionClick,
}: {
  color: string
  onSectionClick?: (type: AccountPageExpandedSection, subType?: string) => void
}) => (
  <div
    className='ui-flex ui-items-center ui-gap-2 ui-py-3'
    onClick={e => { e.stopPropagation(); onSectionClick?.(AccountPageExpandedSection.ORDERS, 'FOOTER') }}
  >
    <Package size={22} color={hexToRgba(color, 0.7)} weight='fill' />
    <p className='ui-text-[24px] ui-font-bold ui-leading-[32px] ui-tracking-[-0.48px]' style={{ color }}>
      Past Orders
    </p>
  </div>
)

// ─── Sort options ──────────────────────────────────────────────────────────────

const SORT_OPTIONS = [
  { label: 'Newest first', sortKey: 'CREATED_AT', reverse: true },
  { label: 'Oldest first', sortKey: 'CREATED_AT', reverse: false },
  { label: 'Highest priced first', sortKey: 'TOTAL_PRICE', reverse: true },
  { label: 'Lowest priced first', sortKey: 'TOTAL_PRICE', reverse: false },
]

// ─── Cancel Dialog ────────────────────────────────────────────────────────────

const CANCEL_REASONS = [
  'Ordered by mistake',
  'Found a better price elsewhere',
  'Delivery is taking too long',
]

const MobileOrderSkeletonCard = () => (
  <div className='ui-flex ui-flex-col'>
    {/* Header — mirrors px-[12px] py-[8px] wrapper of actual order header */}
    <div className='ui-flex ui-flex-col ui-gap-[6px] ui-px-[12px] ui-py-[8px]'>
      <div className='ui-flex ui-gap-[12px] ui-items-center'>
        <div className='ui-flex ui-flex-1 ui-flex-col ui-items-start ui-min-w-0'>
          {/* Row 1: date (flex-1) + status badge (w-[75px] shrink-0) */}
          <div className='ui-flex ui-items-start ui-w-full'>
            <div className='skeleton-loader ui-h-[22px] ui-w-[88px] ui-rounded-[4px]' />
            <div className='skeleton-loader ui-h-[18px] ui-w-[72px] ui-rounded-[100px] ui-shrink-0' />
          </div>
          {/* Row 2: count badge + ITEMS + dot + order# (flex-1) + price (shrink-0) */}
          <div className='ui-flex ui-gap-[8px] ui-items-start ui-w-full ui-mt-[3px]'>
            <div className='ui-flex ui-flex-1 ui-items-center ui-gap-[6px] ui-min-w-0'>
              <div className='skeleton-loader ui-w-[16px] ui-h-[16px] ui-rounded-[3px]' />
              <div className='skeleton-loader ui-h-[11px] ui-w-[33px] ui-rounded-[4px]' />
              <div className='skeleton-loader ui-size-[3px] ui-rounded-full' />
              <div className='skeleton-loader ui-h-[11px] ui-w-[80px] ui-rounded-[4px]' />
            </div>
            <div className='skeleton-loader ui-h-[22px] ui-w-[52px] ui-rounded-[4px] ui-shrink-0' />
          </div>
        </div>
        {/* 3-dot menu button */}
        <div className='skeleton-loader ui-size-[28px] ui-rounded-[4px] ui-shrink-0' />
      </div>
    </div>
    {/* Items card — mirrors gap-[12px] py-[12px] rounded card */}
    <div className='ui-flex ui-flex-col ui-gap-[12px] ui-py-[12px] ui-rounded-[12px]' style={{ backgroundColor: 'rgba(53,53,53,0.04)' }}>
      {[0, 1].map(j => (
        <div key={j} className='ui-flex ui-items-center ui-gap-[16px] ui-px-[12px]'>
          <div className='skeleton-loader ui-shrink-0 ui-size-[64px] ui-rounded-[4px]' />
          <div className='ui-flex ui-flex-1 ui-flex-col ui-gap-[4px] ui-min-w-0'>
            <div className='skeleton-loader ui-h-[22px] ui-w-3/4 ui-rounded-[4px]' />
            <div className='skeleton-loader ui-h-[18px] ui-w-[44px] ui-rounded-[4px]' />
            <div className='skeleton-loader ui-h-[22px] ui-w-[56px] ui-rounded-[4px]' />
          </div>
          <div className='skeleton-loader ui-size-[28px] ui-rounded-[4px] ui-shrink-0' />
        </div>
      ))}
    </div>
  </div>
)

const DesktopOrderSkeletonCard = () => (
  <div className='ui-flex ui-flex-col'>
    {/* Header row — mirrors px-[16px] py-[12px] gap-[12px] actual header */}
    <div className='ui-flex ui-items-center ui-gap-[12px] ui-px-[16px] ui-py-[12px]'>
      {/* Date + items row (flex-1) */}
      <div className='ui-flex ui-flex-1 ui-flex-col ui-gap-[4px] ui-min-w-0'>
        <div className='skeleton-loader ui-h-[22px] ui-w-[100px] ui-rounded-[4px]' />
        <div className='ui-flex ui-items-center ui-gap-[6px]'>
          <div className='skeleton-loader ui-w-[16px] ui-h-[16px] ui-rounded-[3px]' />
          <div className='skeleton-loader ui-h-[11px] ui-w-[33px] ui-rounded-[4px]' />
          <div className='skeleton-loader ui-size-[3px] ui-rounded-full' />
          <div className='skeleton-loader ui-h-[11px] ui-w-[90px] ui-rounded-[4px]' />
        </div>
      </div>
      {/* Status badge */}
      <div className='skeleton-loader ui-h-[22px] ui-w-[80px] ui-rounded-[100px] ui-shrink-0' />
      {/* Total price */}
      <div className='ui-shrink-0 ui-w-[120px] ui-flex ui-justify-end ui-px-[12px]'>
        <div className='skeleton-loader ui-h-[24px] ui-w-[56px] ui-rounded-[4px]' />
      </div>
      {/* View button */}
      <div className='ui-shrink-0 ui-w-[120px] ui-flex ui-justify-end'>
        <div className='skeleton-loader ui-h-[28px] ui-w-[88px] ui-rounded-[4px]' />
      </div>
    </div>
    {/* Items card — mirrors gap-[8px] py-[8px] mb-[8px] actual card */}
    <div className='ui-flex ui-flex-col ui-gap-[8px] ui-py-[8px] ui-mb-[8px] ui-rounded-[12px]' style={{ backgroundColor: 'rgba(53,53,53,0.04)' }}>
      {[0, 1].map(j => (
        <div key={j} className='ui-flex ui-items-center ui-gap-[16px] ui-px-[12px] ui-py-[6px]'>
          {/* Product image */}
          <div className='skeleton-loader ui-shrink-0 ui-size-[60px] ui-rounded-[12px]' />
          {/* Name + qty (flex-1) */}
          <div className='ui-flex ui-flex-1 ui-flex-col ui-gap-[4px] ui-min-w-0'>
            <div className='skeleton-loader ui-h-[22px] ui-w-3/4 ui-rounded-[4px]' />
            <div className='skeleton-loader ui-h-[22px] ui-w-[44px] ui-rounded-[4px]' />
          </div>
          {/* Item price */}
          <div className='ui-shrink-0 ui-flex ui-justify-center ui-px-[12px]'>
            <div className='skeleton-loader ui-h-[22px] ui-w-[52px] ui-rounded-[4px]' />
          </div>
          {/* Buy Again link */}
          <div className='ui-shrink-0 ui-w-[120px] ui-flex ui-justify-end ui-px-[16px]'>
            <div className='skeleton-loader ui-h-[22px] ui-w-[68px] ui-rounded-[4px]' />
          </div>
        </div>
      ))}
    </div>
  </div>
)

const OrdersSkeletonLoader = ({ count = 3, mobileClassName }: { count?: number; mobileClassName?: string }) => (
  <>
    <div className={`@[768px]:ui-hidden ui-space-y-4 ui-flex ui-flex-col ui-spa ${mobileClassName ?? ''}`}>
      {Array.from({ length: count }).map((_, i) => <MobileOrderSkeletonCard key={i} />)}
    </div>
    <div className='ui-hidden @[768px]:ui-flex ui-flex-col ui-space-y-4 ui-gap-0'>
      {Array.from({ length: count }).map((_, i) => <DesktopOrderSkeletonCard key={i} />)}
    </div>
  </>
)

const OrderDetailSkeletonView = () => (
  <div className='ui-flex ui-flex-col ui-gap-[24px] ui-px-4 @[768px]:ui-px-[32px] ui-pt-[16px] @[768px]:ui-pt-[32px] ui-pb-8'>
    {/* Back button + order info */}
    <div className='ui-flex ui-gap-[12px] ui-items-center ui-w-full'>
      <div className='skeleton-loader ui-size-[36px] ui-rounded-[8px] ui-shrink-0' />
      <div className='ui-flex ui-flex-col ui-gap-[4px]'>
        <div className='skeleton-loader ui-h-[22px] ui-w-[56px] ui-rounded-[4px]' />
        <div className='skeleton-loader ui-h-[18px] ui-w-[110px] ui-rounded-[4px]' />
      </div>
    </div>

    {/* Fanned images + status badge + action buttons */}
    <div className='ui-flex ui-flex-col ui-gap-[32px] ui-items-center ui-w-full'>
      {/* Stacked images + status badge */}
      <div className='ui-relative ui-flex ui-items-start ui-h-[86px]'>
        <div className='skeleton-loader ui-size-[64px] ui-rounded-[16px] ui-shrink-0 ui-z-[3]' style={{ marginRight: '-24px' }} />
        <div className='skeleton-loader ui-size-[60px] ui-rounded-[15px] ui-shrink-0 ui-z-[2]' style={{ marginRight: '-24px' }} />
        <div className='skeleton-loader ui-size-[60px] ui-rounded-[15px] ui-shrink-0 ui-z-[1]' />
        <div className='skeleton-loader ui-absolute ui-bottom-0 ui-left-[24px] ui-h-[22px] ui-w-[84px] ui-rounded-[100px]' />
      </div>

      {/* Mobile action buttons — equal-width icon+label blocks */}
      <div className='@[768px]:ui-hidden ui-flex ui-gap-[8px] ui-items-start ui-w-full'>
        <div className='skeleton-loader ui-flex-1 ui-h-[58px] ui-rounded-[8px]' />
        <div className='skeleton-loader ui-flex-1 ui-h-[58px] ui-rounded-[8px]' />
      </div>

      {/* Desktop action buttons — horizontal pill row */}
      <div className='ui-hidden @[768px]:ui-flex ui-gap-[12px]'>
        <div className='skeleton-loader ui-h-[36px] ui-w-[80px] ui-rounded-[8px]' />
        <div className='skeleton-loader ui-h-[36px] ui-w-[80px] ui-rounded-[8px]' />
      </div>
    </div>

    {/* Summary card */}
    <div className='ui-flex ui-flex-col ui-rounded-[12px] ui-overflow-hidden' style={{ backgroundColor: 'rgba(53,53,53,0.04)' }}>
      <div className='ui-px-[16px] ui-py-[12px]'>
        <div className='skeleton-loader ui-h-[22px] ui-w-[72px] ui-rounded-[4px]' />
      </div>
      {[0, 1].map(i => (
        <React.Fragment key={i}>
          <div className='ui-w-full ui-h-[1px]' style={{ backgroundColor: 'rgba(53,53,53,0.08)' }} />
          <div className='ui-flex ui-gap-[8px] ui-items-start ui-p-[12px]'>
            <div className='skeleton-loader ui-shrink-0 ui-size-[40px] ui-rounded-[8px]' />
            <div className='ui-flex ui-flex-1 ui-flex-col ui-gap-[4px] ui-min-w-0'>
              <div className='skeleton-loader ui-h-[22px] ui-w-3/4 ui-rounded-[4px]' />
              <div className='skeleton-loader ui-h-[18px] ui-w-[70px] ui-rounded-[4px]' />
            </div>
            <div className='skeleton-loader ui-h-[22px] ui-w-[48px] ui-rounded-[4px] ui-shrink-0' />
          </div>
        </React.Fragment>
      ))}
      <div className='ui-w-full ui-h-[1px]' style={{ backgroundColor: 'rgba(53,53,53,0.08)' }} />
      {/* Total Paid row */}
      <div className='ui-flex ui-items-center ui-justify-between ui-p-[12px]' style={{ backgroundColor: 'rgba(53,53,53,0.04)' }}>
        <div className='skeleton-loader ui-h-[22px] ui-w-[68px] ui-rounded-[4px]' />
        <div className='skeleton-loader ui-h-[22px] ui-w-[56px] ui-rounded-[4px]' />
      </div>
    </div>

    {/* Info card: payment + address */}
    <div className='ui-flex ui-flex-col ui-rounded-[12px] ui-overflow-hidden' style={{ backgroundColor: 'rgba(53,53,53,0.04)' }}>
      {/* Payment method */}
      <div className='ui-flex ui-gap-[12px] ui-items-start ui-px-[12px] ui-py-[12px]'>
        <div className='skeleton-loader ui-shrink-0 ui-size-[40px] ui-rounded-[8px]' />
        <div className='ui-flex ui-flex-1 ui-flex-col ui-gap-[4px]'>
          <div className='skeleton-loader ui-h-[22px] ui-w-[120px] ui-rounded-[4px]' />
          <div className='skeleton-loader ui-h-[22px] ui-w-[80px] ui-rounded-[4px]' />
        </div>
      </div>
      <div className='ui-w-full ui-h-[1px]' style={{ backgroundColor: 'rgba(53,53,53,0.08)' }} />
      {/* Shipping address */}
      <div className='ui-flex ui-gap-[12px] ui-items-start ui-px-[12px] ui-py-[12px]'>
        <div className='skeleton-loader ui-shrink-0 ui-size-[40px] ui-rounded-[8px]' />
        <div className='ui-flex ui-flex-1 ui-flex-col ui-gap-[4px]'>
          <div className='skeleton-loader ui-h-[22px] ui-w-[110px] ui-rounded-[4px]' />
          <div className='skeleton-loader ui-h-[22px] ui-w-3/4 ui-rounded-[4px]' />
          <div className='skeleton-loader ui-h-[22px] ui-w-1/2 ui-rounded-[4px]' />
        </div>
      </div>
    </div>
  </div>
)

export const CancelDialog = ({
  orderNumber,
  color,
  onClose,
  onSubmit,
}: {
  orderNumber: string
  color: string
  onClose: () => void
  onSubmit: (note: string) => Promise<{ success: boolean; errorMessage?: string | null }>
}) => {
  const [selected, setSelected] = useState<string | null>(null)
  const [otherText, setOtherText] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [result, setResult] = useState<{ success: boolean; error?: string } | null>(null)

  const isOther = selected === 'Other'
  const isValid = selected !== null && (!isOther || otherText.trim().length >= 30)
  const note = isOther ? otherText.trim() : (selected ?? '')
  const charsLeft = Math.max(0, 30 - otherText.trim().length)

  const handleSubmit = async () => {
    if (!isValid || submitting) return
    setSubmitting(true)
    const res = await onSubmit(note)
    setResult({ success: res.success, error: res.errorMessage ?? undefined })
    setSubmitting(false)
  }

  const footerButtons = (
    <div className='ui-flex ui-gap-[8px] ui-pb-[16px] ui-px-[16px]'>
      <button
        className='ui-flex-1 ui-h-[44px] ui-rounded-[8px] ui-text-[14px] ui-font-semibold ui-leading-[22px] ui-tracking-[-0.14px] ui-cursor-pointer'
        style={{ backgroundColor: 'rgba(53,53,53,0.08)', color: 'rgba(53,53,53,0.8)' }}
        onClick={onClose}
      >
        Cancel
      </button>
      <button
        className='ui-flex-1 ui-h-[44px] ui-rounded-[8px] ui-text-[14px] ui-font-semibold ui-leading-[22px] ui-tracking-[-0.14px] ui-transition-opacity ui-duration-150'
        style={{
          backgroundColor: '#353535',
          color: '#fff',
          opacity: isValid && !submitting ? 1 : 0.35,
          cursor: isValid && !submitting ? 'pointer' : 'not-allowed',
        }}
        onClick={handleSubmit}
        disabled={!isValid || submitting}
      >
        {submitting ? 'Submitting…' : 'Submit'}
      </button>
    </div>
  )

  return (
    <AccountPageOverlayPanel onClose={onClose}>
      <div className='ui-flex ui-flex-col ui-gap-[12px]'>
        {/* ── Success state ── */}
        {result?.success && (
          <div className='ui-flex ui-flex-col ui-items-center ui-gap-[16px] ui-text-center ui-px-[24px] ui-pt-[40px] ui-pb-[28px]'>
            <CheckCircle size={48} weight='fill' color='#22c55e' />
            <div>
              <p className='ui-text-[16px] ui-font-semibold ui-leading-[24px] ui-tracking-[-0.16px]' style={{ color: '#353535' }}>Order Cancelled</p>
              <p className='ui-text-[14px] ui-font-medium ui-leading-[22px] ui-tracking-[-0.14px] ui-mt-[4px] ui-opacity-60' style={{ color: '#353535' }}>
                Your order {orderNumber} has been cancelled.
              </p>
            </div>
            <button
              className='ui-w-full ui-h-[44px] ui-rounded-[8px] ui-text-[14px] ui-font-semibold ui-cursor-pointer'
              style={{ backgroundColor: '#353535', color: '#fff' }}
              onClick={onClose}
            >
              Done
            </button>
          </div>
        )}

        {/* ── Error state ── */}
        {result && !result.success && (
          <div className='ui-flex ui-flex-col ui-items-center ui-gap-[16px] ui-text-center ui-px-[24px] ui-pt-[40px] ui-pb-[28px]'>
            <XCircleFill size={48} color='#ef4444' weight='fill' />
            <div>
              <p className='ui-text-[16px] ui-font-semibold ui-leading-[24px] ui-tracking-[-0.16px]' style={{ color: '#353535' }}>Cancellation Failed</p>
              <p className='ui-text-[14px] ui-font-medium ui-leading-[22px] ui-tracking-[-0.14px] ui-mt-[4px] ui-opacity-60' style={{ color: '#353535' }}>
                {result.error ?? 'Something went wrong. Please try again.'}
              </p>
            </div>
            <div className='ui-flex ui-gap-[8px] ui-w-full'>
              <button
                className='ui-flex-1 ui-h-[44px] ui-rounded-[8px] ui-text-[14px] ui-font-semibold ui-cursor-pointer'
                style={{ backgroundColor: 'rgba(53,53,53,0.08)', color: 'rgba(53,53,53,0.8)' }}
                onClick={() => setResult(null)}
              >
                Try Again
              </button>
              <button
                className='ui-flex-1 ui-h-[44px] ui-rounded-[8px] ui-text-[14px] ui-font-semibold ui-cursor-pointer'
                style={{ backgroundColor: '#353535', color: '#fff' }}
                onClick={onClose}
              >
                Close
              </button>
            </div>
          </div>
        )}

        {/* ── Main form ── */}
        {!result && (
          <>
            {/* Header section: px-[12px] outer, title row + subtitle */}
            <div className='ui-flex ui-flex-col ui-items-start ui-px-[12px] ui-shrink-0'>
              {/* Title row */}
              <div className='ui-flex ui-gap-[8px] ui-items-center ui-py-[12px] ui-w-full'>
                <div className='ui-flex-1 ui-min-w-px ui-px-[8px]'>
                  <p className='ui-text-[16px] ui-font-semibold ui-leading-[24px] ui-tracking-[-0.16px]' style={{ color: '#353535' }}>
                    Cancel Order?
                  </p>
                </div>
                <button
                  className='ui-shrink-0 ui-size-[36px] ui-rounded-[8px] ui-flex ui-items-center ui-justify-center ui-cursor-pointer'
                  onClick={onClose}
                >
                  <X size={18} weight='bold' color='rgba(53,53,53,0.5)' />
                </button>
              </div>
              {/* Subtitle */}
              <div className='ui-px-[8px] ui-w-full'>
                <p className='ui-text-[14px] ui-font-medium ui-leading-[22px] ui-tracking-[-0.14px] ui-opacity-60' style={{ color: '#353535' }}>
                  This action can't be undone. Let us know why so we can improve.
                </p>
              </div>
            </div>

            {/* Options section: px-[12px] outer, separator hairlines */}
            <div className='ui-flex ui-flex-col ui-items-start ui-px-[12px] ui-shrink-0'>
              {[...CANCEL_REASONS, 'Other'].map((reason, i) => (
                <div key={reason} className='ui-w-full'>
                  {i > 0 && (
                    <div className='ui-h-px ui-w-full' style={{ backgroundColor: 'rgba(53,53,53,0.08)' }} />
                  )}
                  <button
                    className='ui-flex ui-gap-[8px] ui-items-center ui-justify-center ui-overflow-clip ui-p-[12px] ui-w-full ui-text-left ui-cursor-pointer ui-rounded-[8px]'
                    style={{ backgroundColor: selected === reason ? 'rgba(53,53,53,0.1)' : 'transparent' }}
                    onClick={() => setSelected(reason)}
                  >
                    {/* Radio: filled solid circle when selected, outline otherwise */}
                    <span
                      className='ui-shrink-0 ui-size-[14px] ui-rounded-full'
                      style={{
                        backgroundColor: selected === reason ? '#353535' : 'transparent',
                        border: selected === reason ? 'none' : '1.5px solid rgba(53,53,53,0.35)',
                      }}
                    />
                    <span
                      className='ui-flex-1 ui-min-w-px ui-text-[14px] ui-font-medium ui-leading-[22px] ui-tracking-[-0.14px]'
                      style={{ color: '#000', opacity: selected === reason ? 1 : 0.6 }}
                    >
                      {reason}
                    </span>
                  </button>
                </div>
              ))}
            </div>

            {/* When "Other" selected: light-bg section with textarea + buttons */}
            {isOther ? (
              <div
                className='ui-flex ui-flex-col ui-shrink-0 ui-overflow-clip'
                style={{ backgroundColor: 'rgba(255,255,255,0.3)' }}
              >
                <div className='ui-px-[20px] ui-pt-[16px] ui-pb-[8px] ui-min-h-[120px]'>
                  <textarea
                    value={otherText}
                    onChange={e => setOtherText(e.target.value)}
                    placeholder='Mention your reason...'
                    className='ui-w-full ui-h-full ui-min-h-[80px] ui-text-[14px] ui-font-normal ui-leading-[22px] ui-tracking-[-0.14px] ui-resize-none ui-outline-none ui-bg-transparent'
                    style={{ color: '#353535' }}
                  />
                  {charsLeft > 0 && (
                    <p className='ui-text-[11px] ui-font-medium ui-mt-[4px]' style={{ color: 'rgba(53,53,53,0.4)' }}>
                      {charsLeft} more character{charsLeft !== 1 ? 's' : ''} needed
                    </p>
                  )}
                </div>
                {footerButtons}
              </div>
            ) : (
              footerButtons
            )}
          </>
        )}
      </div>
    </AccountPageOverlayPanel>
  )
}

// ─── Order Detail View ────────────────────────────────────────────────────────

const OrderDetailView = ({
  order,
  onBack,
  onCancel,
}: {
  order: AccountPageOrderDetail
  onBack: () => void
  onCancel?: () => void
}) => {
  const { uiConfig: config, realEstate } = useAccountPageUIContext()
  const isDashboard = realEstate === AccountPageRealEstate.DASHBOARD
  const { action_bar } = config.pages.orders
  const headerColor = action_bar.header_color
  const statusColors = config.pages.orders.details.statuses[order.status]
  const heroImages = order.items.slice(0, 3).map(i => i.imageUrl).filter((u): u is string => Boolean(u))
  const mainTxn = order.transactions[0]

  const showTrackingOption = config.pages.orders.details.show_tracking_option ?? false
  const trackingInfo = showTrackingOption ? (order.trackingInfo ?? []) : []
  const [trackMenuOpen, setTrackMenuOpen] = useState(false)

  // Single tracking entry — open it directly; multiple — toggle the dropdown
  const handleTrackClick = () => {
    // Dashboard preview — the track button is display-only
    if (isDashboard) return
    const single = trackingInfo.length === 1 ? trackingInfo[0] : undefined
    if (single) {
      openTrackingUrl(single.url)
      return
    }
    setTrackMenuOpen(open => !open)
  }

  useEffect(() => {
    if (!trackMenuOpen) return
    const close = () => setTrackMenuOpen(false)
    const timer = setTimeout(() => document.addEventListener('click', close), 0)
    return () => { clearTimeout(timer); document.removeEventListener('click', close) }
  }, [trackMenuOpen])

  const trackDropdown = trackMenuOpen && (
    <div
      className='ui-absolute ui-left-0 ui-top-[calc(100%+4px)] ui-z-50 ui-rounded-[8px] ui-py-[4px] ui-overflow-hidden ui-min-w-[200px]'
      style={{ backgroundColor: 'white', boxShadow: '0px 0px 0px 1px rgba(9,9,11,0.12), 0px 8px 8px -4px rgba(9,9,11,0.06), 0px 2px 6px -2px rgba(9,9,11,0.3)' }}
      onClick={e => e.stopPropagation()}
    >
      {trackingInfo.map((t, i) => (
        <div
          key={`${t.number}-${i}`}
          className='ui-flex ui-flex-col ui-px-[14px] ui-py-[10px] ui-cursor-pointer'
          onClick={() => { openTrackingUrl(t.url); setTrackMenuOpen(false) }}
        >
          <span className='ui-text-[13px] ui-font-semibold ui-leading-[20px]' style={{ color: '#353535' }}>{t.company || 'Track shipment'}</span>
          {t.number && (
            <span className='ui-text-[11px] ui-font-medium ui-leading-[16px] ui-opacity-60' style={{ color: '#353535' }}>{t.number}</span>
          )}
        </div>
      ))}
    </div>
  )

  return (
    <div className='ui-flex ui-flex-col ui-w-full ui-h-full ui-overflow-y-auto ui-gap-[8px] ui-pb-8'>
      {/* Header + hero section */}
      <div className='ui-flex ui-flex-col ui-gap-[24px] ui-items-center ui-px-4 @[768px]:ui-px-[32px] ui-pt-[16px] @[768px]:ui-pt-[32px]'>
        {/* Back button + order info */}
        <div className='ui-flex ui-gap-[12px] ui-items-center ui-w-full'>
          <button
            className='ui-flex ui-items-start ui-justify-end ui-size-[36px] ui-shrink-0 ui-cursor-pointer'
            onClick={onBack}
          >
            <div className='ui-flex ui-flex-1 ui-h-full ui-items-center ui-justify-center ui-rounded-[8px]' style={{ backgroundColor: hexToRgba(headerColor, 0.08) }}>
              <CaretLeft size={18} color={headerColor} weight='bold' />
            </div>
          </button>
          <div className='ui-flex ui-flex-col'>
            <p className='ui-text-[14px] ui-font-semibold ui-leading-[22px] ui-tracking-[-0.14px]' style={{ color: headerColor }}>
              {order.items.length} Item{order.items.length !== 1 ? 's' : ''}
            </p>
            <p className='ui-text-[12px] ui-font-semibold ui-leading-[18px] ui-tracking-[0.48px] ui-uppercase ui-opacity-60' style={{ color: headerColor }}>
              ID: {order.orderNumber}
            </p>
          </div>
        </div>

        {/* Fanned images + status badge + action buttons */}
        <div className='ui-flex ui-flex-col ui-gap-[32px] ui-items-center ui-w-full'>
          {/* Stacked/fanned product images + status pill */}
          <div className='ui-relative ui-flex ui-flex-col ui-gap-[8px] ui-items-start ui-h-[86px]'>
            <div className='ui-flex ui-isolate ui-items-center'>
              {/* First image — front, largest */}
              <div
                className='ui-border-2 ui-border-solid ui-border-white ui-rounded-[16px] ui-shrink-0 ui-size-[64px] ui-z-[3] ui-overflow-hidden'
                style={{ marginRight: '-24px', backgroundColor: hexToRgba(headerColor, 0.08) }}
              >
                {heroImages[0]
                  ? <img src={heroImages[0]} alt='' className='ui-size-full ui-object-cover ui-pointer-events-none' />
                  : <div className='ui-flex ui-items-center ui-justify-center ui-size-full'><Package size={28} color={headerColor} weight='fill' /></div>
                }
              </div>
              {heroImages[1] && (
                <div className='ui-flex ui-items-center ui-justify-center ui-shrink-0 ui-z-[2]' style={{ width: '68px', height: '68px', marginRight: '-24px' }}>
                  <div style={{ transform: 'rotate(8.82deg)' }}>
                    <div className='ui-border-[1.875px] ui-border-solid ui-border-white ui-rounded-[15px] ui-size-[60px] ui-overflow-hidden'>
                      <img src={heroImages[1]} alt='' className='ui-size-full ui-object-cover ui-pointer-events-none' />
                    </div>
                  </div>
                </div>
              )}
              {heroImages[2] && (
                <div className='ui-flex ui-items-center ui-justify-center ui-shrink-0 ui-z-[1]' style={{ width: '64px', height: '64px' }}>
                  <div style={{ transform: 'rotate(-4deg)' }}>
                    <div className='ui-border-[1.875px] ui-border-solid ui-border-white ui-rounded-[15px] ui-size-[60px] ui-overflow-hidden'>
                      <img src={heroImages[2]} alt='' className='ui-size-full ui-object-cover ui-pointer-events-none' />
                    </div>
                  </div>
                </div>
              )}
            </div>
            {/* Status badge — absolutely positioned at bottom */}
            <div
              className='ui-absolute ui-bottom-0 ui-left-[24px] ui-flex ui-items-center ui-justify-end ui-px-[8px] ui-py-[2px] ui-rounded-[100px]'
              style={{ backgroundColor: statusColors.background_color }}
            >
              <StatusIcon status={order.status} color='white' size={14} />
              <span className='ui-text-[14px] ui-font-medium ui-leading-[22px] ui-tracking-[-0.14px] ui-text-white ui-pl-[4px] ui-whitespace-nowrap'>
                {statusLabel(order.status)}
              </span>
            </div>
          </div>

          {/* Action buttons — desktop: horizontal pill row */}
          <div className='ui-hidden @[768px]:ui-flex ui-gap-[12px] ui-items-start'>
            {trackingInfo.length > 0 && (
              <div className='ui-relative'>
                <button
                  className='ui-flex ui-h-[36px] ui-items-start ui-justify-end ui-cursor-pointer'
                  onClick={e => { e.stopPropagation(); handleTrackClick() }}
                >
                  <div className='ui-flex ui-h-full ui-items-center ui-rounded-[8px]' style={{ backgroundColor: hexToRgba(headerColor, 0.08) }}>
                    <div className='ui-flex ui-gap-[4px] ui-h-[28px] ui-items-center ui-justify-center ui-px-[12px] ui-py-[4px]'>
                      <MapPin size={18} color={headerColor} weight='fill' />
                      <span className='ui-text-[14px] ui-font-semibold ui-leading-[22px] ui-tracking-[-0.14px] ui-opacity-80 ui-whitespace-nowrap' style={{ color: headerColor }}>Track</span>
                    </div>
                  </div>
                </button>
                {trackDropdown}
              </div>
            )}
            {onCancel && (
              <button className='ui-flex ui-h-[36px] ui-items-start ui-justify-end ui-cursor-pointer' onClick={onCancel}>
                <div className='ui-flex ui-h-full ui-items-center ui-rounded-[8px]' style={{ backgroundColor: hexToRgba(headerColor, 0.08) }}>
                  <div className='ui-flex ui-gap-[4px] ui-h-[28px] ui-items-center ui-justify-center ui-px-[12px] ui-py-[4px]'>
                    <XCircleFill size={18} color={headerColor} weight='fill' />
                    <span className='ui-text-[14px] ui-font-semibold ui-leading-[22px] ui-tracking-[-0.14px] ui-opacity-80 ui-whitespace-nowrap' style={{ color: headerColor }}>Cancel</span>
                  </div>
                </div>
              </button>
            )}
          </div>

          {/* Action buttons — mobile: equal-width, icon above label */}
          <div className='@[768px]:ui-hidden ui-flex ui-gap-[8px] ui-items-start ui-w-full'>
            {trackingInfo.length > 0 && (
              <div
                className='ui-relative ui-flex-1 ui-rounded-[8px] ui-cursor-pointer'
                style={{ backgroundColor: hexToRgba(headerColor, 0.08) }}
                onClick={e => { e.stopPropagation(); handleTrackClick() }}
              >
                <div className='ui-flex ui-flex-1 ui-flex-col ui-gap-[4px] ui-items-center ui-justify-center ui-px-[12px] ui-py-[8px]'>
                  <MapPin size={22} color={headerColor} weight='fill' />
                  <span className='ui-text-[14px] ui-font-semibold ui-leading-[22px] ui-tracking-[-0.14px] ui-opacity-80 ui-text-center' style={{ color: headerColor }}>Track</span>
                </div>
                {trackDropdown}
              </div>
            )}
            {onCancel && (
              <div className='ui-flex-1 ui-rounded-[8px] ui-cursor-pointer' style={{ backgroundColor: hexToRgba(headerColor, 0.08) }} onClick={onCancel}>
                <div className='ui-flex ui-flex-1 ui-flex-col ui-gap-[4px] ui-items-center ui-justify-center ui-px-[12px] ui-py-[8px]'>
                  <XCircleFill size={22} color={headerColor} weight='fill' />
                  <span className='ui-text-[14px] ui-font-semibold ui-leading-[22px] ui-tracking-[-0.14px] ui-opacity-80 ui-text-center' style={{ color: headerColor }}>Cancel</span>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Summary card */}
      <div
        className='ui-flex ui-flex-col ui-items-start ui-rounded-[12px] ui-overflow-hidden ui-mx-4 @[768px]:ui-mx-[32px]'
        style={{ backgroundColor: hexToRgba(headerColor, 0.06) }}
      >
        <div className='ui-flex ui-items-center ui-justify-center ui-px-[16px] ui-py-[12px] ui-w-full'>
          <p className='ui-flex-1 ui-text-[14px] ui-font-semibold ui-leading-[22px] ui-tracking-[-0.14px]' style={{ color: headerColor }}>Summary</p>
        </div>
        {order.items.map(item => (
          <React.Fragment key={item.id}>
            <div className='ui-w-full ui-h-[1px]' style={{ backgroundColor: hexToRgba(headerColor, 0.08) }} />
            <div className='ui-flex ui-gap-[8px] ui-items-start ui-p-[12px] ui-w-full'>
              <div className='ui-shrink-0 ui-size-[40px] ui-rounded-[8px] ui-overflow-hidden ui-border ui-border-solid' style={{ borderColor: 'rgba(9,9,11,0.08)' }}>
                {item.imageUrl
                  ? <img src={item.imageUrl} alt={item.name} className='ui-size-full ui-object-cover ui-pointer-events-none' />
                  : <div className='ui-size-full' style={{ backgroundColor: hexToRgba(headerColor, 0.08) }} />
                }
              </div>
              <div className='ui-flex ui-flex-1 ui-flex-col ui-items-center ui-justify-center ui-min-h-[40px] ui-min-w-0'>
                <p className='ui-text-[14px] ui-font-medium ui-leading-[22px] ui-tracking-[-0.14px] ui-w-full' style={{ color: headerColor }}>{item.name}</p>
                <p className='ui-text-[12px] ui-font-medium ui-leading-[18px] ui-tracking-[-0.12px] ui-opacity-70 ui-w-full' style={{ color: headerColor }}>
                  {[item.variant, `${item.qty} Qty`].filter(Boolean).join(' · ')}
                </p>
              </div>
              <div className='ui-flex ui-items-center ui-justify-center ui-px-[8px] ui-shrink-0'>
                <p className='ui-text-[14px] ui-font-medium ui-leading-[22px] ui-tracking-[-0.14px] ui-text-right ui-whitespace-nowrap' style={{ color: headerColor }}>{item.price}</p>
              </div>
            </div>
          </React.Fragment>
        ))}
        <div className='ui-w-full ui-h-[1px]' style={{ backgroundColor: hexToRgba(headerColor, 0.08) }} />
        {/* Total Paid row */}
        <div className='ui-flex ui-flex-col ui-gap-[4px] ui-items-center ui-justify-center ui-p-[12px] ui-w-full' style={{ backgroundColor: hexToRgba(headerColor, 0.04) }}>
          <div className='ui-flex ui-items-center ui-justify-between ui-w-full'>
            <div className='ui-flex ui-items-center ui-justify-center ui-px-[6px]'>
              <span className='ui-text-[14px] ui-font-medium ui-leading-[22px] ui-tracking-[-0.14px] ui-opacity-70 ui-whitespace-nowrap' style={{ color: headerColor }}>Total Paid</span>
            </div>
            <div className='ui-flex ui-items-center ui-justify-end'>
              <div className='ui-flex ui-items-center ui-justify-center ui-px-[8px]'>
                <span className='ui-text-[14px] ui-font-semibold ui-leading-[22px] ui-tracking-[-0.14px] ui-whitespace-nowrap' style={{ color: headerColor }}>{order.totalPrice}</span>
              </div>
              <ChevronDown size={16} color={headerColor} strokeWidth={2} className='ui-opacity-60' />
            </div>
          </div>
        </div>
      </div>

      {/* Info card: payment method + addresses */}
      <div
        className='ui-flex ui-flex-col ui-items-start ui-rounded-[12px] ui-mx-4 @[768px]:ui-mx-[32px]'
        style={{ backgroundColor: 'rgba(255,255,255,0.12)' }}
      >
        {/* Payment method row */}
        {mainTxn && (
          <div className='ui-flex ui-flex-col ui-items-start ui-w-full'>
            <div className='ui-flex ui-flex-col ui-items-start ui-px-[12px] ui-w-full'>
              <div className='ui-flex ui-gap-[12px] ui-items-start ui-justify-center ui-py-[12px] ui-w-full'>
                <div className='ui-flex ui-items-center ui-justify-center ui-size-[40px] ui-rounded-[8px] ui-shrink-0 ui-border ui-border-solid' style={{ borderColor: hexToRgba(headerColor, 0.12) }}>
                  <div className='ui-flex ui-items-center ui-opacity-40 ui-p-[4px] ui-rounded-[8px]'>
                    <CreditCard size={22} color={headerColor} weight='fill' />
                  </div>
                </div>
                <div className='ui-flex ui-flex-1 ui-flex-col ui-gap-[4px] ui-items-start ui-justify-center'>
                  <p className='ui-text-[14px] ui-font-semibold ui-leading-[22px] ui-tracking-[-0.14px] ui-w-full' style={{ color: headerColor }}>Payment Method</p>
                  <p className='ui-text-[14px] ui-font-medium ui-leading-[22px] ui-tracking-[-0.14px] ui-opacity-60 ui-w-full' style={{ color: headerColor }}>{mainTxn.gateway}</p>
                </div>
              </div>
            </div>
          </div>
        )}
        {mainTxn && (order.billingAddress || order.shippingAddress) && (
          <div className='ui-w-full ui-h-[1px]' style={{ backgroundColor: hexToRgba(headerColor, 0.08) }} />
        )}
        {/* Addresses row */}
        {(order.billingAddress || order.shippingAddress) && (
          <div className='ui-flex ui-flex-col ui-items-start ui-w-full'>
            <div className='ui-flex ui-flex-col ui-items-start ui-px-[12px] ui-w-full'>
              <div className='ui-flex ui-gap-[12px] ui-items-start ui-justify-center ui-py-[12px] ui-w-full'>
                <div className='ui-flex ui-items-center ui-justify-center ui-size-[40px] ui-rounded-[8px] ui-shrink-0 ui-border ui-border-solid' style={{ borderColor: hexToRgba(headerColor, 0.12) }}>
                  <div className='ui-flex ui-items-center ui-opacity-40 ui-p-[4px] ui-rounded-[8px]'>
                    <MapPin size={22} color={headerColor} weight='fill' />
                  </div>
                </div>
                <div className='ui-flex ui-flex-1 ui-flex-col ui-gap-[12px] ui-items-start ui-justify-center'>
                  {order.billingAddress && (
                    <div className='ui-flex ui-flex-col ui-gap-[4px] ui-items-start ui-pb-[12px] ui-w-full'>
                      <p className='ui-text-[14px] ui-font-semibold ui-leading-[22px] ui-tracking-[-0.14px] ui-w-full' style={{ color: headerColor }}>Billing Address</p>
                      <p className='ui-text-[14px] ui-font-medium ui-leading-[22px] ui-tracking-[-0.14px] ui-opacity-60 ui-w-full' style={{ color: headerColor }}>{order.billingAddress.address1}</p>
                      <p className='ui-text-[14px] ui-font-medium ui-leading-[22px] ui-tracking-[-0.14px] ui-opacity-60 ui-w-full' style={{ color: headerColor }}>
                        {[order.billingAddress.city, order.billingAddress.province, order.billingAddress.zip].filter(Boolean).join(', ')}
                      </p>
                    </div>
                  )}
                  {order.shippingAddress && (
                    <div className='ui-flex ui-flex-col ui-gap-[4px] ui-items-start ui-pb-[12px] ui-w-full'>
                      <p className='ui-text-[14px] ui-font-semibold ui-leading-[22px] ui-tracking-[-0.14px] ui-w-full' style={{ color: headerColor }}>Shipping Address</p>
                      <p className='ui-text-[14px] ui-font-medium ui-leading-[22px] ui-tracking-[-0.14px] ui-opacity-60 ui-w-full' style={{ color: headerColor }}>{order.shippingAddress.address1}</p>
                      <p className='ui-text-[14px] ui-font-medium ui-leading-[22px] ui-tracking-[-0.14px] ui-opacity-60 ui-w-full' style={{ color: headerColor }}>
                        {[order.shippingAddress.city, order.shippingAddress.province, order.shippingAddress.zip].filter(Boolean).join(', ')}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

// ─── Shared: search + sort (container queries handle mobile vs desktop sizing) ──

const OrdersFilterSection = ({
  action_bar,
  activeFilter,
  setActiveFilter,
  onSectionClick,
  sortKey,
  reverse,
  searchQuery,
  onSortChange,
  onSearchChange,
}: {
  action_bar: ReturnType<typeof useAccountPageUIContext>['uiConfig']['pages']['orders']['action_bar']
  activeFilter: string
  setActiveFilter: (tab: string) => void
  onSectionClick?: (type: AccountPageExpandedSection, subType?: string) => void
  sortKey?: string
  reverse?: boolean
  searchQuery?: string
  onSortChange?: (sortKey: string, reverse: boolean) => void
  onSearchChange?: (query: string) => void
}) => {
  const [sortOpen, setSortOpen] = useState(false)
  const [localSearch, setLocalSearch] = useState(searchQuery ?? '')
  const sortRef = useRef<HTMLDivElement>(null)
  const isMounted = useRef(false)

  // Close sort dropdown when clicking outside
  useEffect(() => {
    const handleOutside = (e: MouseEvent) => {
      if (sortRef.current && !sortRef.current.contains(e.target as Node)) setSortOpen(false)
    }
    if (sortOpen) document.addEventListener('mousedown', handleOutside)
    return () => document.removeEventListener('mousedown', handleOutside)
  }, [sortOpen])

  // Debounce search — skip the very first render to avoid a spurious API call on mount
  useEffect(() => {
    if (!isMounted.current) { isMounted.current = true; return }
    const timer = setTimeout(() => { onSearchChange?.(localSearch) }, 400)
    return () => clearTimeout(timer)
  }, [localSearch])

  const activeSortKey = sortKey ?? 'CREATED_AT'
  const activeReverse = reverse ?? true

  return (
    <div
      className='ui-flex ui-items-center ui-gap-[8px]'
      onClick={e => { e.stopPropagation(); onSectionClick?.(AccountPageExpandedSection.ORDERS, 'FOOTER') }}
    >
      {/* Search input */}
      <div
        className='ui-flex ui-items-center ui-gap-[8px] ui-h-[36px] ui-px-[8px] ui-rounded-[4px] ui-flex-1 @[768px]:ui-flex-none @[768px]:ui-w-[340px]'
        style={{ backgroundColor: action_bar.background_color, border: '1.5px solid rgba(0,0,0,0.1)', borderRadius: action_bar.tab_radius }}
        onClick={e => e.stopPropagation()}
      >
        <Search size={16} color={action_bar.header_color} strokeWidth={2} className='ui-opacity-50 ui-shrink-0' />
        <input
          type='text'
          placeholder='Search...'
          value={localSearch}
          onChange={e => setLocalSearch(e.target.value)}
          className='ui-flex-1 ui-min-w-0 ui-bg-transparent ui-border-none ui-outline-none ui-truncate placeholder:ui-opacity-50 ui-text-[14px] ui-font-normal ui-leading-[22px] ui-tracking-[-0.14px]'
          style={{ color: action_bar.header_color }}
        />
        {localSearch && (
          <button
            className='ui-shrink-0 ui-opacity-40 ui-cursor-pointer ui-flex ui-items-center'
            onClick={e => { e.stopPropagation(); setLocalSearch(''); onSearchChange?.('') }}
          >
            <XCircle size={14} color={action_bar.header_color} />
          </button>
        )}
      </div>
      {/* Sort button + dropdown */}
      <div className='ui-relative ui-shrink-0' ref={sortRef}>
        <div
          className='ui-flex ui-items-center ui-justify-center ui-w-[36px] ui-h-[36px] ui-rounded-[4px] ui-cursor-pointer'
          style={{ backgroundColor: action_bar.background_color, border: '1.5px solid rgba(0,0,0,0.1)', borderRadius: action_bar.tab_radius }}
          onClick={e => { e.stopPropagation(); setSortOpen(v => !v) }}
        >
          <SortAscending size={18} color={action_bar.header_color} className='ui-opacity-60' />
        </div>
        {sortOpen && (
          <div
            className='ui-absolute ui-right-0 ui-top-[calc(100%+4px)] ui-z-50 ui-w-[280px] ui-rounded-[4px] ui-py-[6px] ui-overflow-hidden'
            style={{
              backgroundColor: action_bar.background_color,
              boxShadow: '0px 0px 0px 1px rgba(9,9,11,0.12), 0px 16px 12px -6px rgba(9,9,11,0.06), 0px 2px 10px -6px rgba(9,9,11,0.48)',
            }}
            onClick={e => e.stopPropagation()}
          >
            {SORT_OPTIONS.map(option => {
              const isSelected = option.sortKey === activeSortKey && option.reverse === activeReverse
              return (
                <div
                  key={option.label}
                  className='ui-flex ui-items-center ui-gap-[8px] ui-px-[16px] ui-py-[12px] ui-cursor-pointer'
                  onClick={() => { onSortChange?.(option.sortKey, option.reverse); setSortOpen(false) }}
                >
                  <div className='ui-flex ui-items-center ui-justify-center ui-shrink-0 ui-size-[20px]'>
                    {isSelected && <Check size={18} weight='bold' color={action_bar.header_color} />}
                  </div>
                  <span
                    className={`ui-text-[14px] ui-font-semibold ui-leading-[22px] ui-tracking-[-0.14px]${isSelected ? '' : ' ui-opacity-70'}`}
                    style={{ color: action_bar.header_color }}
                  >
                    {option.label}
                  </span>
                </div>
              )
            })}
          </div>
        )}
      </div>
    </div>
  )
}

// ─── Main component ───────────────────────────────────────────────────────────

type OrdersPageUIProps = {
  /**
   * Layout this instance is visible in; undefined while hidden. Merchant custom
   * components mount only in the visible layout, so their scripts run once.
   */
  visibleLayout?: 'mobile' | 'desktop'
}

const OrdersPageUI = ({ visibleLayout }: OrdersPageUIProps) => {
  const { uiConfig: config, data, actions, realEstate } = useAccountPageUIContext()
  const isDashboard = realEstate === AccountPageRealEstate.DASHBOARD
  const { action_bar, details } = config.pages.orders
  const showCancelOption = details.show_cancel_option ?? false
  const showTrackingOption = details.show_tracking_option ?? false
  const header_color = action_bar.header_color
  const [activeFilter, setActiveFilter] = useState('All')
  const orders = data?.ordersPage?.orders ?? []
  const isLoading = data?.ordersPage?.isLoading ?? false
  const emailGateState = data?.ordersPage?.emailGateState ?? 'verified'
  const unverifiedEmail = data?.ordersPage?.unverifiedEmail
  const primaryEmail = data?.profilePage?.user?.primaryEmail
  const emailIdentifiers = data?.profilePage?.user?.emailIdentifiers ?? []
  const ordersSortKey = data?.ordersPage?.sortKey
  const ordersReverse = data?.ordersPage?.reverse
  const ordersSearchQuery = data?.ordersPage?.searchQuery
  const hasNextPage = data?.ordersPage?.hasNextPage ?? false
  const isLoadingMoreOrders = data?.ordersPage?.isLoadingMoreOrders ?? false

  const { openCancel } = useCancelDialog()

  const [detailOrder, setDetailOrder] = useState<AccountPageOrderDetail | null>(null)
  const [detailLoading, setDetailLoading] = useState(false)
  const [openActionMenuId, setOpenActionMenuId] = useState<string | null>(null)
  const [openTrackMenuId, setOpenTrackMenuId] = useState<string | null>(null)
  const [openItemMenuId, setOpenItemMenuId] = useState<string | null>(null)

  // ── State-dependent content (shared logic) ───────────────────────────────

  const gateContent = (() => {
    // While the profile is still loading we don't yet know the correct email
    // verification state. Show the skeleton instead of flashing the email gate.
    const isProfileLoading = data?.profilePage?.isLoading ?? false
    if (isProfileLoading && emailGateState !== 'verified') {
      return <OrdersSkeletonLoader count={3} mobileClassName='ui-mt-2' />
    }

    if (emailGateState === 'no_email') {
      return (
        <EmptyOrdersState
          description='Your orders are tied to the email you used at checkout. Try adding an email to see them here.'
          headerColor={header_color}
          actions={[{ label: 'Add Email', onClick: () => actions.onOpenEmailFlow?.({ returnToOrders: true }) }]}
        />
      )
    }
    if (emailGateState === 'unverified') {
      const hasMultipleEmails = emailIdentifiers.length > 1
      return (
        <EmptyOrdersState
          description={hasMultipleEmails
            ? 'Your orders are tied to the email you used at checkout. Please verify or select another email to see them here.'
            : 'Your orders are tied to the email you used at checkout. Please verify your email to see them here.'
          }
          email={unverifiedEmail}
          headerColor={header_color}
          actions={[
            { label: 'Verify Email', onClick: () => actions.onOpenEmailFlow?.({ returnToOrders: true, initialEmail: unverifiedEmail }) },
            ...(hasMultipleEmails ? [{ label: 'Switch Email', showIcon: true, onClick: () => actions.onOpenEmailFlow?.({ returnToOrders: false }) }] : []),
          ]}
        />
      )
    }

    if (isLoading) {
      return <OrdersSkeletonLoader count={3} mobileClassName='ui-mt-2' />
    }
    
    if (orders.length === 0) {
      if (ordersSearchQuery?.trim()) {
        return (
          <div className='ui-flex ui-flex-col ui-items-center ui-justify-center ui-h-full ui-w-full ui-gap-[8px] ui-py-12 ui-px-6'>
            <div className='ui-flex ui-items-center ui-justify-center ui-size-[120px]'>
              <img src='https://image.shopflo.com/shopping-cart.png' alt='No orders' className='ui-size-[120px] ui-object-contain' />
            </div>
            <p
              className='ui-text-[14px] ui-font-semibold ui-leading-[22px] ui-tracking-[-0.14px] ui-text-center ui-max-w-[316px]'
              style={{ color: header_color }}
            >
              {`Can't find an order for '${ordersSearchQuery.trim()}'`}
            </p>
          </div>
        )
      }
      return (
        <EmptyOrdersState
          description='Your orders are tied to the email you used at checkout. Try switching to that email to see them here.'
          headerColor={header_color}
          actions={[{ label: 'Switch Email', showIcon: true, onClick: () => actions.onOpenEmailFlow?.({ returnToOrders: false }) }]}
        />
      )
    }
    return null // orders present — render normally below
  })()

  const handleViewOrder = async (order: AccountPageOrder) => {
    setOpenActionMenuId(null)
    const numericId = order.id.split('/').pop() ?? order.id
    if (!actions.onViewOrderDetail) { actions.onViewOrder?.(order); return }
    setDetailLoading(true)
    const detail = await actions.onViewOrderDetail(numericId)
    setDetailLoading(false)
    if (detail) setDetailOrder(detail)
  }

  const handleCancelOrder = (order: AccountPageOrder) => {
    setOpenActionMenuId(null)
    // Dashboard preview — the cancel button is display-only
    if (isDashboard) return
    openCancel({ id: order.id.split('/').pop() ?? order.id, orderNumber: order.orderNumber, color: header_color })
  }

  // Desktop list Track button: single entry opens directly, multiple toggles a per-row dropdown
  const handleTrackOrder = (order: AccountPageOrder) => {
    // Dashboard preview — the track button is display-only
    if (isDashboard) return
    const info = order.trackingInfo ?? []
    const single = info.length === 1 ? info[0] : undefined
    if (single) {
      openTrackingUrl(single.url)
      return
    }
    setOpenTrackMenuId(openTrackMenuId === order.id ? null : order.id)
  }

  useEffect(() => {
    if (!openActionMenuId) return
    const close = () => setOpenActionMenuId(null)
    const timer = setTimeout(() => document.addEventListener('click', close), 0)
    return () => { clearTimeout(timer); document.removeEventListener('click', close) }
  }, [openActionMenuId])

  useEffect(() => {
    if (!openTrackMenuId) return
    const close = () => setOpenTrackMenuId(null)
    const timer = setTimeout(() => document.addEventListener('click', close), 0)
    return () => { clearTimeout(timer); document.removeEventListener('click', close) }
  }, [openTrackMenuId])

  useEffect(() => {
    if (!openItemMenuId) return
    const close = () => setOpenItemMenuId(null)
    const timer = setTimeout(() => document.addEventListener('click', close), 0)
    return () => { clearTimeout(timer); document.removeEventListener('click', close) }
  }, [openItemMenuId])

  return (
    <>
      {detailOrder ? (
        <OrderDetailView
          order={detailOrder}
          onBack={() => setDetailOrder(null)}
          onCancel={showCancelOption && detailOrder.fulfillmentStatus === 'UNFULFILLED'
            ? () => {
                // Dashboard preview — the cancel button is display-only
                if (isDashboard) return
                openCancel({ id: detailOrder.id.split('/').pop() ?? detailOrder.id, orderNumber: detailOrder.orderNumber, color: header_color, onSuccess: () => setDetailOrder(null) })
              }
            : undefined
          }
        />
      ) : detailLoading ? (
        <OrderDetailSkeletonView />
      ) : (<>
      {visibleLayout && (
        <div className='ui-px-4 @[768px]:ui-px-[40px]'>
          <AccountCustomComponentSlot page='ORDERS' position='TOP_SECTION' />
        </div>
      )}
      {/* Mobile layout — hidden at ≥768px */}
      <div className='@[768px]:ui-hidden ui-flex-col ui-justify-between ui-py-2 ui-px-4' onClick={() => actions.onSectionClick?.(AccountPageExpandedSection.ORDERS, 'PRIMARY_BUTTON')}>
        <div className='ui-flex-col ui-min-h-[600px]'>
          <PastOrdersHeader color={header_color} onSectionClick={actions.onSectionClick} />
          {emailGateState === 'verified' && (
            <OrdersFilterSection
              action_bar={action_bar}
              activeFilter={activeFilter}
              setActiveFilter={setActiveFilter}
              onSectionClick={actions.onSectionClick}
              sortKey={ordersSortKey}
              reverse={ordersReverse}
              searchQuery={ordersSearchQuery}
              onSortChange={actions.onSortChange}
              onSearchChange={actions.onSearchChange}
            />
          )}
          {visibleLayout === 'mobile' && <AccountCustomComponentSlot page='ORDERS' position='HEADER_SECTION' />}

          {/* Mobile state content */}
          {gateContent ?? (
            <div className='ui-flex ui-flex-col ui-mt-2'>
              {orders.map((order, index) => {
                const statusColors = details.statuses[order.status]
                return (
                  <React.Fragment key={order.id}>
                  {index > 0 && <div className='ui-h-px' style={{ backgroundColor: hexToRgba(details.group_header_color, 0.12) }} />}
                  <div className='ui-flex ui-flex-col ui-mb-[12px]'>
                    {/* Order header */}
                    <div className='ui-flex ui-flex-col ui-gap-[6px] ui-px-[12px] ui-py-[8px]'>
                      <div className='ui-flex ui-gap-[12px] ui-items-center'>
                        <div className='ui-flex ui-flex-1 ui-flex-col ui-items-start ui-min-w-0'>
                          {/* Row 1: date + status */}
                          <div className='ui-flex ui-items-start ui-w-full'>
                            <p
                              className='ui-flex-1 ui-text-[14px] ui-font-semibold ui-leading-[22px] ui-tracking-[-0.14px] ui-min-w-0'
                              style={{ color: details.group_header_color }}
                            >
                              {order.date}
                            </p>
                            <div className='ui-flex ui-items-center ui-py-[2px] ui-rounded-[100px] ui-shrink-0 ui-w-[75px] ui-justify-end'>
                              <StatusIcon status={order.status} color={statusColors.background_color} size={14} />
                              <span
                                className='ui-text-[12px] ui-font-medium ui-leading-[18px] ui-tracking-[-0.12px] ui-whitespace-nowrap ui-px-[4px]'
                                style={{ color: statusColors.background_color }}
                              >
                                {statusLabel(order.status)}
                              </span>
                            </div>
                          </div>
                          {/* Row 2: items count + order number + total */}
                          <div className='ui-flex ui-gap-[8px] ui-items-start ui-w-full'>
                            <div className='ui-flex ui-flex-1 ui-items-center ui-gap-[8px] ui-min-w-0'>
                              <div className='ui-flex ui-items-center ui-gap-[6px]'>
                                <div
                                  className='ui-flex ui-items-center ui-justify-center ui-rounded-[4px] ui-w-[16px]'
                                  style={{ backgroundColor: hexToRgba(details.group_header_color, 0.4) }}
                                >
                                  <span className='ui-text-[10px] ui-font-semibold ui-leading-[16px] ui-tracking-[0.4px] ui-uppercase ui-text-center ui-opacity-80 ui-text-white'>
                                    {order.items.length}
                                  </span>
                                </div>
                                <span className='ui-text-[10px] ui-font-semibold ui-leading-[16px] ui-tracking-[0.4px] ui-uppercase ui-opacity-60' style={{ color: details.group_header_color }}>
                                  ITEMS
                                </span>
                              </div>
                              <div className='ui-size-[3px] ui-rounded-full ui-opacity-40' style={{ backgroundColor: details.group_header_color }} />
                              <span className='ui-flex-1 ui-text-[10px] ui-font-semibold ui-leading-[16px] ui-tracking-[0.4px] ui-uppercase ui-opacity-60 ui-truncate' style={{ color: details.group_header_color }}>
                                {order.orderNumber}
                              </span>
                            </div>
                            <span className='ui-text-[16px] ui-font-bold ui-leading-[24px] ui-tracking-[-0.16px] ui-whitespace-nowrap ui-px-[4px]' style={{ color: details.group_header_color }}>
                              {order.totalPrice}
                            </span>
                          </div>
                        </div>
                        {/* Order action menu */}
                        <div className='ui-relative ui-shrink-0' onClick={e => e.stopPropagation()}>
                          <div
                            className='ui-flex ui-items-center ui-justify-center ui-rounded-[4px] ui-size-[28px] ui-cursor-pointer'
                            style={{ backgroundColor: 'rgba(53,53,53,0.08)' }}
                            onClick={e => { e.stopPropagation(); setOpenActionMenuId(openActionMenuId === order.id ? null : order.id) }}
                          >
                            <DotsThreeVertical size={22} color={details.group_header_color} weight='regular' />
                          </div>
                          {openActionMenuId === order.id && (
                            <div
                              className='ui-absolute ui-right-0 ui-top-[calc(100%+4px)] ui-z-50 ui-rounded-[8px] ui-py-[4px] ui-overflow-hidden ui-min-w-[160px]'
                              style={{ backgroundColor: 'white', boxShadow: '0px 0px 0px 1px rgba(9,9,11,0.12), 0px 8px 8px -4px rgba(9,9,11,0.06), 0px 2px 6px -2px rgba(9,9,11,0.3)' }}
                              onClick={e => e.stopPropagation()}
                            >
                              <div
                                className='ui-flex ui-items-center ui-gap-[10px] ui-px-[14px] ui-py-[10px] ui-cursor-pointer'
                                onClick={() => handleViewOrder(order)}
                              >
                                <span className='ui-text-[13px] ui-font-semibold ui-leading-[20px]'>View Details</span>
                              </div>
                              {/* Track — single entry opens directly; multiple entries listed one per courier */}
                              {showTrackingOption && (() => {
                                const tracking = order.trackingInfo ?? []
                                if (!tracking.length) return null
                                const single = tracking.length === 1 ? tracking[0] : undefined
                                if (single) {
                                  return (
                                    <div
                                      className='ui-flex ui-items-center ui-gap-[10px] ui-px-[14px] ui-py-[10px] ui-cursor-pointer'
                                      onClick={() => { setOpenActionMenuId(null); if (!isDashboard) openTrackingUrl(single.url) }}
                                    >
                                      <span className='ui-text-[13px] ui-font-semibold ui-leading-[20px]'>Track Order</span>
                                    </div>
                                  )
                                }
                                return tracking.map((t, i) => (
                                  <div
                                    key={`${t.number}-${i}`}
                                    className='ui-flex ui-flex-col ui-px-[14px] ui-py-[10px] ui-cursor-pointer'
                                    onClick={() => { setOpenActionMenuId(null); if (!isDashboard) openTrackingUrl(t.url) }}
                                  >
                                    <span className='ui-text-[13px] ui-font-semibold ui-leading-[20px]'>Track — {t.company || 'shipment'}</span>
                                    {t.number && (
                                      <span className='ui-text-[11px] ui-font-medium ui-leading-[16px] ui-opacity-60'>{t.number}</span>
                                    )}
                                  </div>
                                ))
                              })()}
                              {showCancelOption && order.fulfillmentStatus === 'UNFULFILLED' && (
                                <div
                                  className='ui-flex ui-items-center ui-gap-[10px] ui-px-[14px] ui-py-[10px] ui-cursor-pointer'
                                  onClick={() => handleCancelOrder(order)}
                                >
                                  <span className='ui-text-[13px] ui-font-semibold ui-leading-[20px]' style={{ color: '#ef4444' }}>Cancel Order</span>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Order items card */}
                    <div
                      className='ui-flex ui-flex-col ui-gap-[12px] ui-py-[12px]'
                      style={{ backgroundColor: details.background_color, borderRadius: details.card_radius || '12px', ...getBorderStyle(details.sides, details.card_border, details.border_color) }}
                    >
                      {order.items.map(item => {
                        const itemMenuKey = `${order.id}|${item.id}`
                        return (
                        <div
                          key={item.id}
                          className='ui-flex ui-items-center ui-gap-[16px] ui-px-[12px] ui-cursor-pointer'
                          onClick={e => {
                            e.stopPropagation()
                            if (item.productUrl) {
                              window.open(item.productUrl, '_blank', 'noopener,noreferrer')
                            } else {
                              actions?.onBuyAgain?.(item, order)
                            }
                          }}
                        >
                          {/* Product image */}
                          <div
                            className='ui-shrink-0 ui-rounded-[4px] ui-overflow-hidden ui-size-[64px]'
                            style={{ backgroundColor: `${details.order_info_color}22` }}
                          >
                            {item.imageUrl && <img src={item.imageUrl} alt={item.name} className='ui-size-full ui-object-cover' />}
                          </div>

                          {/* Product info */}
                          <div className='ui-flex ui-flex-1 ui-flex-col ui-gap-[4px] ui-items-start ui-justify-center ui-min-w-0'>
                            <div className='ui-flex ui-flex-col ui-items-start'>
                              <span
                                className='ui-text-[14px] ui-font-semibold ui-leading-[22px] ui-tracking-[-0.14px]'
                                style={{ color: details.order_info_color }}
                              >
                                {truncateMiddle(item.name)}
                              </span>
                              <div className='ui-flex ui-items-center ui-gap-[6px]'>
                                <span className='ui-text-[12px] ui-font-medium ui-leading-[18px] ui-tracking-[-0.12px] ui-opacity-60' style={{ color: details.order_info_color }}>
                                  {item.qty} Qty
                                </span>
                                {item.variant && (
                                  <>
                                    <div className='ui-size-[3px] ui-rounded-full ui-opacity-20' style={{ backgroundColor: details.order_info_color }} />
                                    <span className='ui-text-[12px] ui-font-medium ui-leading-[18px] ui-tracking-[-0.12px] ui-opacity-60' style={{ color: details.order_info_color }}>
                                      {item.variant}
                                    </span>
                                  </>
                                )}
                              </div>
                            </div>
                            <span className='ui-text-[14px] ui-font-medium ui-leading-[22px] ui-tracking-[-0.14px] ui-opacity-80 ui-whitespace-nowrap' style={{ color: details.order_info_color }}>
                              {item.price}
                            </span>
                          </div>

                          {/* Dots menu */}
                          {item.productUrl ? (
                            <div className='ui-relative ui-shrink-0' onClick={e => e.stopPropagation()}>
                              <div
                                className='ui-flex ui-items-center ui-justify-center ui-rounded-[4px] ui-size-[28px] ui-cursor-pointer'
                                onClick={e => { e.stopPropagation(); setOpenItemMenuId(openItemMenuId === itemMenuKey ? null : itemMenuKey) }}
                              >
                                <DotsThreeVertical size={22} color={details.order_info_color} weight='regular' className='ui-opacity-40' />
                              </div>
                              {openItemMenuId === itemMenuKey && (
                                <div
                                  className='ui-absolute ui-right-0 ui-top-[calc(100%+4px)] ui-z-50 ui-rounded-[8px] ui-py-[4px] ui-overflow-hidden ui-min-w-[140px]'
                                  style={{ backgroundColor: 'white', boxShadow: '0px 0px 0px 1px rgba(9,9,11,0.12), 0px 8px 8px -4px rgba(9,9,11,0.06), 0px 2px 6px -2px rgba(9,9,11,0.3)' }}
                                  onClick={e => e.stopPropagation()}
                                >
                                  <div
                                    className='ui-flex ui-items-center ui-gap-[10px] ui-px-[14px] ui-py-[10px] ui-cursor-pointer'
                                    onClick={() => { window.open(item.productUrl!, '_blank', 'noopener,noreferrer'); setOpenItemMenuId(null) }}
                                  >
                                    <span className='ui-text-[13px] ui-font-semibold ui-leading-[20px]'>Buy Now</span>
                                  </div>
                                </div>
                              )}
                            </div>
                          ) : (
                            <div className='ui-flex ui-items-center ui-justify-center ui-rounded-[4px] ui-size-[28px] ui-shrink-0'>
                              <DotsThreeVertical size={22} color={details.order_info_color} weight='regular' className='ui-opacity-40' />
                            </div>
                          )}
                        </div>
                        )
                      })}
                    </div>
                  </div>
                  </React.Fragment>
                )
              })}
              {/* Load more button — mobile */}
              {hasNextPage && (
                <div className='ui-flex ui-justify-center ui-py-4'>
                  <button
                    onClick={() => actions.onLoadMoreOrders?.()}
                    disabled={isLoadingMoreOrders}
                    className='ui-flex ui-items-center ui-gap-2 ui-px-5 ui-py-2 ui-rounded-full ui-text-[13px] ui-font-medium ui-border ui-border-solid ui-transition-opacity'
                    style={{
                      color: header_color,
                      borderColor: hexToRgba(header_color, 0.2),
                      backgroundColor: hexToRgba(header_color, 0.05),
                      opacity: isLoadingMoreOrders ? 0.6 : 1,
                    }}
                  >
                    {isLoadingMoreOrders ? (
                      <>
                        <svg className='ui-animate-spin' width='14' height='14' viewBox='0 0 14 14' fill='none'>
                          <circle cx='7' cy='7' r='5.5' stroke='currentColor' strokeWidth='1.5' strokeLinecap='round' strokeDasharray='8 20' />
                        </svg>
                        Loading...
                      </>
                    ) : 'Load more orders'}
                  </button>
                </div>
              )}
              {/* Email footer — mobile */}
              {primaryEmail && (
                <div className='ui-flex ui-items-center ui-justify-center ui-gap-[6px] ui-py-4 ui-px-4'>
                  <span className='ui-text-[11px] ui-font-medium ui-opacity-50 ui-text-center' style={{ color: details.group_header_color }}>
                    Showing orders for <span className='ui-opacity-80'>{primaryEmail}</span>
                  </span>
                  {!isDashboard && (
                    <button
                      onClick={() => actions.onOpenEmailFlow?.({ returnToOrders: false })}
                      className='ui-text-[11px] ui-font-semibold ui-px-2 ui-py-0.5 ui-rounded-full ui-border ui-border-solid ui-shrink-0 ui-cursor-pointer'
                      style={{ color: details.group_header_color, borderColor: hexToRgba(details.group_header_color, 0.2) }}
                    >
                      Switch
                    </button>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Desktop layout — hidden below 768px */}
      <div
        className='ui-hidden @[768px]:ui-flex ui-flex-col ui-justify-between ui-px-[40px] ui-w-full'
        onClick={() => actions.onSectionClick?.(AccountPageExpandedSection.ORDERS, 'PRIMARY_BUTTON')}
      >
        <div className='ui-flex-col ui-gap-[24px] ui-min-h-[600px] ui-w-full ui-overflow-y-auto'>
          <div className='ui-flex ui-items-center ui-justify-between ui-w-full'>
            <PastOrdersHeader color={header_color} onSectionClick={actions.onSectionClick} />
            {emailGateState === 'verified' && (
              <OrdersFilterSection
                action_bar={action_bar}
                activeFilter={activeFilter}
                setActiveFilter={setActiveFilter}
                onSectionClick={actions.onSectionClick}
                sortKey={ordersSortKey}
                reverse={ordersReverse}
                searchQuery={ordersSearchQuery}
                onSortChange={actions.onSortChange}
                onSearchChange={actions.onSearchChange}
              />
            )}
          </div>
          {visibleLayout === 'desktop' && <AccountCustomComponentSlot page='ORDERS' position='HEADER_SECTION' />}

          {/* Desktop state content */}
          {gateContent ?? (
            <div className='ui-flex ui-flex-col ui-gap-0'>
              {orders.map((order, index) => {
                const statusColors = details.statuses[order.status]
                return (
                  <React.Fragment key={order.id}>
                  {index > 0 && <div className='ui-h-px' style={{ backgroundColor: hexToRgba(details.group_header_color, 0.1) }} />}
                  <div className='ui-flex ui-flex-col'>
                    {/* Order header row */}
                    <div className='ui-flex ui-items-center ui-gap-[12px] ui-px-[16px] ui-py-[12px]'>
                      {/* Date + items count + order number */}
                      <div className='ui-flex ui-flex-1 ui-flex-col ui-gap-[4px] ui-items-start ui-min-w-0'>
                        <p
                          className='ui-text-[14px] ui-font-semibold ui-leading-[22px] ui-tracking-[-0.14px] ui-whitespace-nowrap'
                          style={{ color: details.group_header_color }}
                        >
                          {order.date}
                        </p>
                        <div className='ui-flex ui-items-center ui-gap-[8px]'>
                          <div className='ui-flex ui-items-center ui-gap-[6px]'>
                            <div
                              className='ui-flex ui-items-center ui-justify-center ui-rounded-[4px] ui-w-[16px]'
                              style={{ backgroundColor: hexToRgba(details.group_header_color, 0.4) }}
                            >
                              <span className='ui-text-[10px] ui-font-semibold ui-leading-[16px] ui-tracking-[0.4px] ui-uppercase ui-text-center ui-opacity-80' style={{ color: details.group_header_color }}>
                                {order.items.length}
                              </span>
                            </div>
                            <span className='ui-text-[10px] ui-font-semibold ui-leading-[16px] ui-tracking-[0.4px] ui-uppercase ui-opacity-60' style={{ color: details.group_header_color }}>
                              ITEMS
                            </span>
                          </div>
                          <div className='ui-size-[3px] ui-rounded-full ui-opacity-40' style={{ backgroundColor: details.group_header_color }} />
                          <span className='ui-text-[10px] ui-font-semibold ui-leading-[16px] ui-tracking-[0.4px] ui-uppercase ui-opacity-60' style={{ color: details.group_header_color }}>
                            {order.orderNumber}
                          </span>
                        </div>
                      </div>

                      {/* Status */}
                      <div className='ui-flex ui-items-center ui-px-[8px] ui-py-[2px] ui-rounded-[100px] ui-shrink-0'>
                        <StatusIcon status={order.status} color={statusColors.background_color} size={14} />
                        <span className='ui-text-[14px] ui-font-medium ui-leading-[22px] ui-tracking-[-0.14px] ui-whitespace-nowrap ui-pl-[4px]' style={{ color: statusColors.background_color }}>
                          {statusLabel(order.status)}
                        </span>
                      </div>

                      {/* Total price */}
                      <div className='ui-shrink-0 ui-w-[120px] ui-flex ui-justify-end ui-px-[12px]'>
                        <span className='ui-text-[16px] ui-font-bold ui-leading-[24px] ui-tracking-[-0.16px] ui-whitespace-nowrap' style={{ color: details.group_header_color }}>
                          {order.totalPrice}
                        </span>
                      </div>

                      {/* Track / View / Cancel action */}
                      <div className='ui-flex ui-justify-end ui-items-center ui-relative ui-min-w-[128px] ui-gap-[8px]' onClick={e => e.stopPropagation()}>
                        {showTrackingOption && (order.trackingInfo ?? []).length > 0 && (
                          <div className='ui-relative'>
                            <button
                              className='ui-flex ui-items-center ui-justify-center ui-h-[28px] ui-px-[14px] ui-rounded-[4px] ui-cursor-pointer'
                              style={{ backgroundColor: details.button.background_color || 'rgba(53,53,53,0.08)' }}
                              onClick={e => { e.stopPropagation(); handleTrackOrder(order) }}
                            >
                              <span className='ui-text-[14px] ui-font-semibold ui-leading-[22px] ui-tracking-[-0.14px] ui-opacity-80 ui-whitespace-nowrap' style={{ color: details.button.text_color || details.group_header_color }}>Track</span>
                            </button>
                            {openTrackMenuId === order.id && (
                              <div
                                className='ui-absolute ui-right-0 ui-top-[calc(100%+4px)] ui-z-50 ui-rounded-[8px] ui-py-[4px] ui-overflow-hidden ui-min-w-[200px]'
                                style={{ backgroundColor: 'white', boxShadow: '0px 0px 0px 1px rgba(9,9,11,0.12), 0px 8px 8px -4px rgba(9,9,11,0.06), 0px 2px 6px -2px rgba(9,9,11,0.3)' }}
                                onClick={e => e.stopPropagation()}
                              >
                                {(order.trackingInfo ?? []).map((t, i) => (
                                  <div
                                    key={`${t.number}-${i}`}
                                    className='ui-flex ui-flex-col ui-px-[14px] ui-py-[10px] ui-cursor-pointer'
                                    onClick={() => { openTrackingUrl(t.url); setOpenTrackMenuId(null) }}
                                  >
                                    <span className='ui-text-[13px] ui-font-semibold ui-leading-[20px]' style={{ color: '#353535' }}>{t.company || 'Track shipment'}</span>
                                    {t.number && (
                                      <span className='ui-text-[11px] ui-font-medium ui-leading-[16px] ui-opacity-60' style={{ color: '#353535' }}>{t.number}</span>
                                    )}
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        )}
                        <div
                          className='ui-flex ui-items-center ui-rounded-[4px] ui-overflow-hidden'
                          style={{ backgroundColor: details.button.background_color || 'rgba(53,53,53,0.08)' }}
                        >
                          <button
                            className='ui-flex ui-items-center ui-justify-center ui-h-[28px] ui-px-[14px] ui-cursor-pointer'
                            onClick={() => handleViewOrder(order)}
                          >
                            <span className='ui-text-[14px] ui-font-semibold ui-leading-[22px] ui-tracking-[-0.14px] ui-opacity-80 ui-whitespace-nowrap' style={{ color: details.button.text_color || details.group_header_color }}>View Order</span>
                          </button>
                          {showCancelOption && order.fulfillmentStatus === 'UNFULFILLED' && (<>
                            <div className='ui-self-stretch ui-w-[1px]' style={{ backgroundColor: `${details.group_header_color}20` }} />
                            <div className='ui-flex ui-w-5 ui-items-center ui-justify-center'>
                            <button
                              className='ui-flex ui-items-center ui-justify-center ui-size-[16px] ui-cursor-pointer'
                              onClick={e => { e.stopPropagation(); setOpenActionMenuId(openActionMenuId === order.id ? null : order.id) }}
                            >
                              <ChevronDown size={16} color={details.button.text_color || details.group_header_color} strokeWidth={2} className='ui-opacity-60' />
                            </button>
                            </div>
                          </>)}
                        </div>
                        {showCancelOption && openActionMenuId === order.id && order.fulfillmentStatus === 'UNFULFILLED' && (
                          <div
                            className='ui-absolute ui-right-0 ui-top-[calc(100%+4px)] ui-z-50 ui-rounded-[8px] ui-py-[4px] ui-overflow-hidden ui-min-w-[180px]'
                            style={{ backgroundColor: 'white', boxShadow: '0px 0px 0px 1px rgba(9,9,11,0.12), 0px 8px 8px -4px rgba(9,9,11,0.06), 0px 2px 6px -2px rgba(9,9,11,0.3)' }}
                            onClick={e => e.stopPropagation()}
                          >
                            <div
                              className='ui-flex ui-items-center ui-px-[14px] ui-py-[10px] ui-cursor-pointer'
                              onClick={() => handleCancelOrder(order)}
                            >
                              <span className='ui-text-[13px] ui-font-semibold ui-leading-[20px]' style={{ color: '#ef4444' }}>Cancel Order</span>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Order items card */}
                    <div
                      className='ui-flex ui-flex-col ui-gap-[8px] ui-py-[8px] ui-mb-[12px]'
                      style={{ backgroundColor: details.background_color, borderRadius: details.card_radius, ...getBorderStyle(details.sides, details.card_border, details.border_color) }}
                    >
                      {order.items.map(item => (
                        <div
                          key={item.id}
                          className='ui-flex ui-items-center ui-gap-[16px] ui-px-[12px] ui-py-[6px] ui-cursor-pointer'
                          onClick={e => {
                            e.stopPropagation()
                            if (item.productUrl) {
                              window.open(item.productUrl, '_blank', 'noopener,noreferrer')
                            } else {
                              actions?.onBuyAgain?.(item, order)
                            }
                          }}
                        >
                          {/* Product image */}
                          <div
                            className='ui-shrink-0 ui-rounded-[12px] ui-overflow-hidden ui-size-[60px]'
                            style={{ backgroundColor: `${details.order_info_color}22` }}
                          >
                            {item.imageUrl && <img src={item.imageUrl} alt={item.name} className='ui-size-full ui-object-cover' />}
                          </div>

                          {/* Product name + variant */}
                          <div className='ui-flex ui-flex-1 ui-flex-col ui-items-start ui-justify-center ui-min-w-0'>
                            <span
                              className='ui-text-[14px] ui-font-semibold ui-leading-[22px] ui-tracking-[-0.14px] ui-whitespace-nowrap ui-opacity-90'
                              style={{ color: details.order_info_color }}
                            >
                              {item.name}
                            </span>
                            <div className='ui-flex ui-items-center ui-gap-[6px]'>
                              <span className='ui-text-[14px] ui-font-normal ui-leading-[22px] ui-tracking-[-0.14px] ui-opacity-60' style={{ color: details.order_info_color }}>
                                {item.qty} Qty
                              </span>
                              {item.variant && (
                                <>
                                  <div className='ui-size-[3px] ui-rounded-full ui-opacity-20' style={{ backgroundColor: details.order_info_color }} />
                                  <span className='ui-text-[14px] ui-font-normal ui-leading-[22px] ui-tracking-[-0.14px] ui-opacity-60' style={{ color: details.order_info_color }}>
                                    {item.variant}
                                  </span>
                                </>
                              )}
                            </div>
                          </div>

                          {/* Item price */}
                          <div className='ui-shrink-0 ui-flex ui-justify-center ui-px-[12px]'>
                            <span className='ui-text-[14px] ui-font-medium ui-leading-[22px] ui-tracking-[-0.14px] ui-opacity-60 ui-whitespace-nowrap' style={{ color: details.order_info_color }}>
                              {item.price}
                            </span>
                          </div>

                          {/* Buy Again */}
                          <div className='ui-shrink-0 ui-w-[120px] ui-flex ui-justify-end ui-px-[16px]'>
                            <button
                              className='ui-text-[14px] ui-font-semibold ui-leading-[22px] ui-tracking-[-0.14px] ui-opacity-70 ui-whitespace-nowrap ui-underline ui-cursor-pointer'
                              style={{ color: details.order_info_color }}
                              onClick={e => {
                                console.log('Buy Again clicked', item, order)
                                e.stopPropagation()
                                if (item.productUrl) {
                                  window.open(item.productUrl, '_blank', 'noopener,noreferrer')
                                } else {
                                  actions?.onBuyAgain?.(item, order)
                                }
                              }}
                            >
                              Buy Again
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                  </React.Fragment>
                )
              })}
              {/* Load more button — desktop */}
              {hasNextPage && (
                <div className='ui-flex ui-justify-center ui-py-4'>
                  <button
                    onClick={() => actions.onLoadMoreOrders?.()}
                    disabled={isLoadingMoreOrders}
                    className='ui-flex ui-items-center ui-gap-2 ui-px-5 ui-py-2 ui-rounded-full ui-text-[13px] ui-font-medium ui-border ui-border-solid ui-transition-opacity'
                    style={{
                      color: header_color,
                      borderColor: hexToRgba(header_color, 0.2),
                      backgroundColor: hexToRgba(header_color, 0.05),
                      opacity: isLoadingMoreOrders ? 0.6 : 1,
                    }}
                  >
                    {isLoadingMoreOrders ? (
                      <>
                        <svg className='ui-animate-spin' width='14' height='14' viewBox='0 0 14 14' fill='none'>
                          <circle cx='7' cy='7' r='5.5' stroke='currentColor' strokeWidth='1.5' strokeLinecap='round' strokeDasharray='8 20' />
                        </svg>
                        Loading...
                      </>
                    ) : 'Load more orders'}
                  </button>
                </div>
              )}
              {/* Email footer — desktop */}
              {primaryEmail && (
                <div className='ui-flex ui-items-center ui-justify-center ui-gap-[8px] ui-pt-4 ui-px-[20px]'>
                  <span className='ui-text-[13px] ui-font-medium ui-opacity-50' style={{ color: details.group_header_color }}>
                    Showing orders for <span className='ui-opacity-80'>{primaryEmail}</span>
                  </span>
                  {!isDashboard && (
                    <button
                      onClick={() => actions.onOpenEmailFlow?.({ returnToOrders: false })}
                      className='ui-text-[13px] ui-font-semibold ui-px-3 ui-py-1 ui-rounded-full ui-border ui-border-solid ui-shrink-0 ui-cursor-pointer'
                      style={{ color: details.group_header_color, borderColor: hexToRgba(details.group_header_color, 0.2) }}
                    >
                      Switch
                    </button>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
      {visibleLayout && (
        <div className='ui-px-4 @[768px]:ui-px-[40px]'>
          <AccountCustomComponentSlot page='ORDERS' position='BOTTOM_SECTION' />
        </div>
      )}
      </>)}
    </>
  )
}

export default OrdersPageUI
